import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Switch, FormControlLabel, Alert,
  CircularProgress, Divider, Stack, Chip
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import { useUser } from '../context/UserContext';
import { usersApi } from '../services/api';

export default function PreferencesPage() {
  const { user, refresh } = useUser();
  const [dailySummaryEnabled, setDailySummaryEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Initialise from /me when user loads
  useEffect(() => {
    if (user) {
      setDailySummaryEnabled((user as any).daily_summary_enabled !== 0);
      setLoading(false);
    }
  }, [user]);

  const isManagerOrAdmin = user?.role === 'admin' || user?.role === 'manager';

  const handleToggleDailySummary = async (next: boolean) => {
    setSaving(true);
    setMessage(null);
    setDailySummaryEnabled(next); // optimistic
    try {
      await usersApi.updateMyPreferences({ daily_summary_enabled: next });
      setMessage({ type: 'success', text: next ? 'Daily summary email enabled.' : 'Daily summary email disabled.' });
      setTimeout(() => setMessage(null), 3000);
      refresh();
    } catch {
      setDailySummaryEnabled(!next); // revert on failure
      setMessage({ type: 'error', text: 'Failed to update preference. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box maxWidth={720}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Preferences
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage how you receive notifications from COMP.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
            <EmailIcon sx={{ color: '#162566' }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Email Notifications
            </Typography>
          </Stack>

          <Divider sx={{ mb: 2 }} />

          {!isManagerOrAdmin ? (
            <Alert severity="info">
              No email preferences are currently available for your role. Email summaries are only sent to managers and administrators.
            </Alert>
          ) : (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={dailySummaryEnabled}
                    onChange={(e) => handleToggleDailySummary(e.target.checked)}
                    disabled={saving}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Daily contract activity summary
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receive an email at 8:00 AM listing contracts that were loaded, edited, or had documents uploaded the previous day.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', m: 0 }}
              />

              <Box mt={2} pl={6}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label="Sent daily, 8:00 AM" />
                  <Chip size="small" label={`Sent to: ${user?.email}`} variant="outlined" />
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  You can also unsubscribe directly from any summary email — every email contains a one-click unsubscribe link in the footer.
                </Typography>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
